import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { toast } from 'sonner';
import {
  Button,
  Checkbox,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@staff-app/ui';
import { ADMIN_ROLES, type AdminRole, type AdminUser } from '@staff-app/shared-types';
import { STAFF_UPDATE_ADMIN_USER_ROLE } from '@staff-app/graphql-client';
import { useAuthStore } from '@/stores/auth-store';
import { useDialogSubmitState } from '@/hooks/useDialogSubmitState';

interface Props {
  user: AdminUser | null;
  onOpenChange: (open: boolean) => void;
}

interface UpdateResult {
  staffUpdateAdminUserRole: AdminUser;
}

const ROLE_OPTIONS: { value: AdminRole; label: string; description: string }[] = [
  { value: ADMIN_ROLES.ROOT, label: 'Root', description: 'Full access' },
  { value: ADMIN_ROLES.ENGINEER, label: 'Engineer', description: 'Network + My Account' },
  { value: ADMIN_ROLES.SUPPORT, label: 'Support', description: 'Customers + Network + My Account' },
  {
    value: ADMIN_ROLES.FULFILLMENT,
    label: 'Fulfillment',
    // Says what it grants AND what it does not: this role issues spendable Shopify codes with no
    // invoice behind them, so nobody should be handed it under the impression it is a mild
    // read-only tier. It is narrow, not weak.
    description: 'Devices Fulfillment only — issues demo device coupons',
  },
  {
    value: ADMIN_ROLES.BILLING,
    label: 'Billing',
    // Narrow, like Fulfillment: it can switch a customer's screens off from the review queue.
    description: 'Dunning Review only — review queue, suspended list, exclusions',
  },
  { value: ADMIN_ROLES.READONLY, label: 'Read only', description: 'My Account only' },
];

function sortedRoles(roles: AdminRole[]): string {
  return [...roles].sort().join(',');
}

export function EditRoleDialog({ user, onOpenChange }: Props) {
  const me = useAuthStore((s) => s.adminUser);
  const [roles, setRoles] = useState<AdminRole[]>(user?.roles ?? [ADMIN_ROLES.ENGINEER]);

  useEffect(() => {
    if (user) setRoles(user.roles ?? [ADMIN_ROLES.ENGINEER]);
  }, [user]);

  const [updateMutation, { loading }] = useMutation<UpdateResult>(STAFF_UPDATE_ADMIN_USER_ROLE, {
    refetchQueries: ['staffAdminUsers'],
  });

  const isSelfDemoteFromRoot =
    !!user && !!me && user.email === me.email &&
    user.roles?.includes(ADMIN_ROLES.ROOT) &&
    !roles.includes(ADMIN_ROLES.ROOT);

  const isDirty = !!user && sortedRoles(roles) !== sortedRoles(user.roles ?? []);

  const { disabled: submitDisabled, disabledTooltip } = useDialogSubmitState({
    isSubmitting: loading,
    isDirty,
    validationError: isSelfDemoteFromRoot ? 'You cannot demote yourself from root.' : '',
  });

  const toggleRole = (role: AdminRole) => {
    setRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  };

  const handleSubmit = async () => {
    if (!user) return;
    try {
      await updateMutation({
        variables: { email: user.email, roles: roles.map((r) => r.toUpperCase() as AdminRole) },
      });
      toast.success('Roles updated');
      onOpenChange(false);
    } catch (err) {
      const msg = (err as { message?: string })?.message ?? 'Unknown error';
      toast.error('Role update failed', { description: msg });
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-role-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{'Change roles'}</DialogTitle>
        </DialogHeader>

        {user ? (
          <div className="flex flex-col gap-4 py-2">
            <div className="rounded-md bg-muted px-3 py-2 text-sm font-medium">{user.email}</div>
            <div className="space-y-2">
              <Label>{'Roles'}</Label>
              <div className="grid grid-cols-2 gap-2">
                {ROLE_OPTIONS.map(({ value, label, description }) => {
                  const id = `edit-role-${value}`;
                  return (
                    <div
                      key={value}
                      className="flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer"
                      onClick={() => toggleRole(value)}
                    >
                      <Checkbox
                        id={id}
                        checked={roles.includes(value)}
                        onCheckedChange={() => toggleRole(value)}
                      />
                      <div className="flex flex-col">
                        <Label htmlFor={id} className="cursor-pointer font-medium">
                          {label}
                        </Label>
                        <span className="text-xs text-muted-foreground">{description}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">{'Cancel'}</Button>
          </DialogClose>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={submitDisabled ? 0 : -1}>
                  <Button
                    onClick={handleSubmit}
                    disabled={submitDisabled}
                    data-testid="edit-role-submit"
                  >
                    {loading ? 'Saving…' : 'Save'}
                  </Button>
                </span>
              </TooltipTrigger>
              {disabledTooltip ? <TooltipContent>{disabledTooltip}</TooltipContent> : null}
            </Tooltip>
          </TooltipProvider>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
